import { useState } from 'react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'

type ViewMode = 'plain' | 'parsed'

interface InlineCommentsViewProps {
  code: string
  targetLanguage?: string
}

interface InsightBlock {
  id: string
  surface: string
  concept: string
  paradigm: string
  codeAfter: string
}

interface CodeSegment {
  type: 'code' | 'insight'
  content: string
  insight?: InsightBlock
}

/**
 * Strips markdown code block markers (```language and ```) from code
 */
function stripCodeBlockMarkers(code: string): string {
  let cleaned = code.replace(/^```[\w-]*\n?/m, '')
  cleaned = cleaned.replace(/\n?```\s*$/m, '')
  return cleaned.trim()
}

/**
 * Parse code into segments: regular code and insight blocks
 * Insight format:
 * // ┌ S: [surface comparison]
 * // │ C: [concept explanation]
 * // └ P: [paradigm explanation]
 * [code line being explained]
 */
function parseCodeIntoSegments(code: string): CodeSegment[] {
  const segments: CodeSegment[] = []
  const lines = code.split('\n')
  let i = 0
  let codeBuffer: string[] = []

  while (i < lines.length) {
    const line = lines[i]

    // Skip legend line
    if (line.trim().match(/^\/\/\s*\[S=Surface\s+C=Concept\s+P=Paradigm\]/i)) {
      i++
      continue
    }

    // Check if this is the start of an insight block
    if (line.trim().startsWith('// ┌ S:') || line.trim().startsWith('// // ┌ S:')) {
      // Flush any accumulated code
      if (codeBuffer.length > 0) {
        segments.push({
          type: 'code',
          content: codeBuffer.join('\n')
        })
        codeBuffer = []
      }

      // Parse insight block - aggressively remove ALL markers
      // Use simple approach: remove everything up to and including the first label
      const surface = line
        .replace(/^.*?┌.*?S:\s*/, '')  // Remove everything up to S:
        .trim()

      const concept = (lines[i + 1] || '')
        .replace(/^.*?│.*?C:\s*/, '')  // Remove everything up to C:
        .trim()

      const paradigm = (lines[i + 2] || '')
        .replace(/^.*?└.*?P:\s*/, '')  // Remove everything up to P:
        .trim()

      const codeAfter = lines[i + 3] || ''

      segments.push({
        type: 'insight',
        content: '',
        insight: {
          id: `insight-${i}`,
          surface,
          concept,
          paradigm,
          codeAfter
        }
      })

      i += 4 // Skip the 3 comment lines + code line
    } else {
      codeBuffer.push(line)
      i++
    }
  }

  // Flush remaining code
  if (codeBuffer.length > 0) {
    segments.push({
      type: 'code',
      content: codeBuffer.join('\n')
    })
  }

  return segments
}

/**
 * Collapsible insight component
 */
function CollapsibleInsight({
  insight,
  isExpanded,
  onToggle,
  targetLanguage
}: {
  insight: InsightBlock
  isExpanded: boolean
  onToggle: () => void
  targetLanguage: string
}) {
  return (
    <div className="pl-0 py-1 mb-1">
      {/* Surface line - always visible, clickable */}
      <div
        className="flex items-start gap-2 cursor-pointer hover:bg-slate-800/20 pr-2 py-1 rounded transition-colors"
        onClick={onToggle}
      >
        <span className="text-slate-600 select-none text-xs mt-1 flex-shrink-0">
          {isExpanded ? '▼' : '▶'}
        </span>
        <div className="flex-1 font-mono text-sm flex gap-1">
          <span className="text-slate-600">S:</span>
          <span className="text-emerald-400">{insight.surface}</span>
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="mt-1 space-y-1 text-sm animate-fadeIn">
          <div className="font-mono pl-4 flex gap-1">
            <span className="text-slate-600">C:</span>
            <span className="text-emerald-400">{insight.concept}</span>
          </div>
          <div className="font-mono pl-4 flex gap-1">
            <span className="text-slate-600">P:</span>
            <span className="text-emerald-400">{insight.paradigm}</span>
          </div>
        </div>
      )}

      {/* Code line that follows the insight */}
      <div className="mt-2">
        <SyntaxHighlighter
          language={targetLanguage.toLowerCase()}
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '0.875rem'
          }}
          PreTag="div"
        >
          {insight.codeAfter}
        </SyntaxHighlighter>
      </div>
    </div>
  )
}

/**
 * Plain editor view - displays raw code with comments (no parsing)
 * Just the content part, header is managed by parent
 */
function PlainEditorView({
  code,
  targetLanguage
}: {
  code: string
  targetLanguage: string
}) {
  return (
    <div className="flex-1 overflow-auto px-6 py-4">
      <SyntaxHighlighter
        language={targetLanguage.toLowerCase()}
        style={vscDarkPlus}
        showLineNumbers={true}
        customStyle={{
          margin: 0,
          padding: '1rem',
          background: 'transparent',
          fontSize: '0.875rem',
          lineHeight: '1.6'
        }}
        PreTag="div"
      >
        {code}
      </SyntaxHighlighter>
    </div>
  )
}

/**
 * Displays code with collapsible inline INSIGHT comment blocks
 */
export default function InlineCommentsView({
  code,
  targetLanguage = 'Rust'
}: InlineCommentsViewProps) {
  const cleanedCode = stripCodeBlockMarkers(code)
  const segments = parseCodeIntoSegments(cleanedCode)

  // View mode state (default to plain editor)
  const [viewMode, setViewMode] = useState<ViewMode>('plain')

  // Track which insights are expanded
  const [expandedInsights, setExpandedInsights] = useState<Set<string>>(new Set())
  const [expandAll, setExpandAll] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(cleanedCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const toggleInsight = (id: string) => {
    setExpandedInsights(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleExpandAll = () => {
    if (expandAll) {
      // Collapse all
      setExpandedInsights(new Set())
      setExpandAll(false)
    } else {
      // Expand all
      const allIds = segments
        .filter(s => s.type === 'insight')
        .map(s => s.insight!.id)
      setExpandedInsights(new Set(allIds))
      setExpandAll(true)
    }
  }

  // If plain mode, render PlainEditorView directly
  if (viewMode === 'plain') {
    return (
      <div className="flex flex-col h-full bg-[#1e1e1e]">
        {/* Minimal header - invisible UI philosophy */}
        <div className="flex items-center justify-end gap-1 px-6 py-3 border-b border-slate-800/50">
          <button
            onClick={() => setViewMode('plain')}
            className={`text-xs px-2 py-1 transition-colors ${
              viewMode === 'plain'
                ? 'text-orange-400'
                : 'text-slate-500 hover:text-slate-300'
            }`}
            data-testid="view-mode-plain"
            aria-label="Switch to plain editor view"
          >
            Plain Editor
          </button>
          <span className="text-slate-700">|</span>
          <button
            onClick={() => setViewMode('parsed')}
            className={`text-xs px-2 py-1 transition-colors ${
              viewMode === 'parsed'
                ? 'text-orange-400'
                : 'text-slate-500 hover:text-slate-300'
            }`}
            data-testid="view-mode-parsed"
            aria-label="Switch to parsed insights view"
          >
            Parsed
          </button>
          <span className="text-slate-700">|</span>
          <button
            onClick={handleCopy}
            className="text-xs px-2 py-1 text-slate-500 hover:text-slate-300 transition-colors"
            data-testid="inline-comments-copy-button"
            aria-label="Copy code to clipboard"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        {/* Plain editor content */}
        <PlainEditorView code={cleanedCode} targetLanguage={targetLanguage} />
      </div>
    )
  }

  // Parsed mode - original collapsible UI
  return (
    <div className="flex flex-col h-full bg-[#1e1e1e]">
      {/* Minimal header - invisible UI philosophy */}
      <div className="flex items-center justify-end gap-1 px-6 py-3 border-b border-slate-800/50">
        <button
          onClick={() => setViewMode('plain')}
          className={`text-xs px-2 py-1 transition-colors ${
            viewMode === 'plain'
              ? 'text-orange-400'
              : 'text-slate-500 hover:text-slate-300'
          }`}
          data-testid="view-mode-plain"
          aria-label="Switch to plain editor view"
        >
          Plain Editor
        </button>
        <span className="text-slate-700">|</span>
        <button
          onClick={() => setViewMode('parsed')}
          className={`text-xs px-2 py-1 transition-colors ${
            viewMode === 'parsed'
              ? 'text-orange-400'
              : 'text-slate-500 hover:text-slate-300'
          }`}
          data-testid="view-mode-parsed"
          aria-label="Switch to parsed insights view"
        >
          Parsed
        </button>
        <span className="text-slate-700">|</span>
        <button
          onClick={handleCopy}
          className="text-xs px-2 py-1 text-slate-500 hover:text-slate-300 transition-colors"
          data-testid="inline-comments-copy-button"
          aria-label="Copy code to clipboard"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <span className="text-slate-700">|</span>
        <button
          onClick={handleExpandAll}
          className="text-xs px-2 py-1 text-slate-500 hover:text-slate-300 transition-colors"
          data-testid="expand-all-button"
          aria-label={expandAll ? 'Collapse all insights' : 'Expand all insights'}
        >
          {expandAll ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {/* Code display with collapsible insights */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {segments.map((segment, index) => {
          if (segment.type === 'insight' && segment.insight) {
            return (
              <CollapsibleInsight
                key={segment.insight.id}
                insight={segment.insight}
                isExpanded={expandedInsights.has(segment.insight.id)}
                onToggle={() => toggleInsight(segment.insight!.id)}
                targetLanguage={targetLanguage}
              />
            )
          } else {
            return (
              <div key={`code-${index}`} className="mb-2">
                <SyntaxHighlighter
                  language={targetLanguage.toLowerCase()}
                  style={vscDarkPlus}
                  showLineNumbers={false}
                  customStyle={{
                    margin: 0,
                    padding: 0,
                    background: 'transparent',
                    fontSize: '0.875rem',
                    lineHeight: '1.6'
                  }}
                  PreTag="div"
                >
                  {segment.content}
                </SyntaxHighlighter>
              </div>
            )
          }
        })}
      </div>
    </div>
  )
}
